// src/components/cards/ProjectExpandedView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../../utils/api';
import { Logger } from '../../utils/logger';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BaseExpandedCard from './BaseExpandedCard';

const logger = new Logger('ProjectExpandedView');

function normalise(text) {
  if (!text) return '';
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

const ProjectExpandedView = ({ open, onClose, project }) => {
  const [loading, setLoading]         = useState(false);
  const [teamRows, setTeamRows]       = useState([]);
  const [error, setError]             = useState(null);
  const [expanded, setExpanded]       = useState(new Set());
  // weeklyData: { [employee_id]: { loading, weeks: [{label, hours}] } }
  const [weeklyData, setWeeklyData]   = useState({});

  const projectId   = project?.project_id;
  const projectName = project?.name || project?.project_name || '';
  const clientName  = project?.client_name || '';

  const apiProjectId = projectId
    ? (String(projectId).startsWith('P') ? String(projectId) : `P${projectId}`)
    : null;

  // Reset on close
  useEffect(() => {
    if (!open) {
      setExpanded(new Set());
      setWeeklyData({});
    }
  }, [open]);

  // Fetch summary (assigned + billed totals) when modal opens
  useEffect(() => {
    if (!open || !apiProjectId) return;

    setLoading(true);
    setError(null);
    setTeamRows([]);

    Promise.all([
      apiFetch(`projects/${apiProjectId}/team-hours`)
        .then(r => r.ok ? r.json() : {})
        .catch(() => ({})),
      apiFetch(`projects/${apiProjectId}/assignments`)
        .then(r => r.ok ? r.json() : [])
        .catch(() => [])
    ])
      .then(([billedMap, assignments]) => {
        const assignedMap = {};
        const nameMap     = {};
        const roleMap     = {};
        for (const a of (assignments || [])) {
          const eid = a.employee_id;
          if (!eid) continue;
          const hrs = parseFloat(a.total_hours_assigned) ||
            (parseFloat(a.weekly_hours || 0) * parseFloat(a.role_duration_weeks || 0));
          assignedMap[eid] = (assignedMap[eid] || 0) + hrs;
          if (a.employee_name) nameMap[eid] = a.employee_name;
          if (a.role && !roleMap[eid]) roleMap[eid] = a.role;
        }

        const allIds = new Set([...Object.keys(assignedMap), ...Object.keys(billedMap)]);
        const rows = Array.from(allIds).map(eid => {
          const billed   = billedMap[eid];
          const assigned = assignedMap[eid];
          // If assigned but no billing records found, treat billed as 0 so variance is shown
          const billedVal = billed != null ? billed : (assigned != null ? 0 : null);
          return {
            employee_id: eid,
            name:     nameMap[eid] || eid,
            role:     roleMap[eid] || '—',
            assigned: assigned != null ? Math.round(assigned * 10) / 10 : null,
            billed:   billedVal != null ? Math.round(billedVal * 10) / 10 : null,
            isOver:   assigned != null && billedVal != null && billedVal > assigned
          };
        });

        rows.sort((a, b) => {
          if (a.isOver && !b.isOver) return -1;
          if (!a.isOver && b.isOver) return 1;
          return a.name.localeCompare(b.name);
        });

        setTeamRows(rows);
      })
      .catch(err => {
        logger.error('Failed to load timesheet data:', err);
        setError('Could not load timesheet data');
      })
      .finally(() => setLoading(false));
  }, [open, apiProjectId]);

  // Lazy-fetch weekly breakdown for a single employee when their row is expanded
  const fetchWeeklyForEmployee = useCallback((employeeId) => {
    if (weeklyData[employeeId]) return; // already fetched or loading

    setWeeklyData(prev => ({ ...prev, [employeeId]: { loading: true, weeks: [] } }));

    const normPid  = apiProjectId?.toUpperCase();
    const normName = normalise(projectName);

    apiFetch(`employees/${employeeId}/timesheet-summary`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        const weekly = data.weekly_timesheets || [];

        // Each weekly entry has a projects[] array — filter to this project
        const byMonth = new Map();
        for (const week of weekly) {
          const projects = week.projects || [];
          const match = projects.find(p =>
            (normPid  && p.project_id?.toUpperCase() === normPid) ||
            (normName && normalise(p.project_name)   === normName)
          );
          if (!match) continue;

          const weekEnding = week.week_ending;
          if (!weekEnding) continue;
          let d;
          try { d = new Date(weekEnding); } catch { continue; }
          if (isNaN(d.getTime())) continue;

          const key   = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          byMonth.set(key, { key, label, hours: Math.round((match.hours || 0) * 10) / 10 });
        }

        const weeks = Array.from(byMonth.values()).sort((a, b) => b.key.localeCompare(a.key));
        setWeeklyData(prev => ({ ...prev, [employeeId]: { loading: false, weeks } }));
      })
      .catch(err => {
        logger.warn(`Weekly fetch failed for ${employeeId}:`, err);
        setWeeklyData(prev => ({ ...prev, [employeeId]: { loading: false, weeks: [] } }));
      });
  }, [apiProjectId, projectName, weeklyData]);

  const toggleExpand = useCallback((employeeId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
        fetchWeeklyForEmployee(employeeId);
      }
      return next;
    });
  }, [fetchWeeklyForEmployee]);

  const totalBilled   = teamRows.reduce((s, r) => s + (r.billed   ?? 0), 0);
  const totalAssigned = teamRows.reduce((s, r) => s + (r.assigned  ?? 0), 0);

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title="Timesheets"
      subtitle={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {clientName && (
            <Typography sx={{ fontSize: '0.9rem', color: '#03DAC6', fontWeight: 500 }}>
              {clientName}
            </Typography>
          )}
          {clientName && projectName && (
            <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>|</Typography>
          )}
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
            {projectName}
          </Typography>
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: '0.75rem', color: 'error.main', p: 2 }}>{error}</Typography>
      ) : teamRows.length === 0 ? (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center', py: 4 }}>
          No timesheet data found for this project
        </Typography>
      ) : (
        <Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}>
                <TableCell sx={{ ...headerCell, width: 28, px: 0.5 }} />
                <TableCell sx={headerCell}>Employee</TableCell>
                <TableCell sx={headerCell}>Role</TableCell>
                <TableCell align="right" sx={headerCell}>Assigned</TableCell>
                <TableCell align="right" sx={headerCell}>Billed</TableCell>
                <TableCell align="right" sx={headerCell}>Variance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamRows.map(row => {
                const variance = row.billed != null && row.assigned != null
                  ? Math.round((row.billed - row.assigned) * 10) / 10
                  : null;
                const isExpanded = expanded.has(row.employee_id);
                const wd = weeklyData[row.employee_id];

                return (
                  <React.Fragment key={row.employee_id}>
                    {/* Summary row */}
                    <TableRow
                      onClick={() => toggleExpand(row.employee_id)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
                    >
                      <TableCell sx={{ px: 0.5, py: 0.5, border: 0 }}>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {isExpanded
                            ? <ExpandLessIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                            : <ExpandMoreIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                          }
                        </IconButton>
                      </TableCell>
                      <TableCell sx={bodyCell}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#64B5F6' }}>
                          {row.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                          {row.employee_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCell, color: 'text.secondary' }}>{row.role}</TableCell>
                      <TableCell align="right" sx={bodyCell}>
                        {row.assigned != null ? `${row.assigned}h` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...bodyCell, color: row.isOver ? '#CF6679' : '#03DAC6', fontWeight: 600 }}>
                        {row.billed != null ? `${row.billed}h` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...bodyCell, color: row.isOver ? '#CF6679' : variance != null && variance < 0 ? '#03DAC6' : 'text.secondary' }}>
                        {variance != null ? `${variance > 0 ? '+' : ''}${variance}h` : '—'}
                      </TableCell>
                    </TableRow>

                    {/* Weekly breakdown rows */}
                    <TableRow sx={{ p: 0 }}>
                      <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} unmountOnExit>
                          {wd?.loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 5, py: 1 }}>
                              <CircularProgress size={12} />
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Loading...</Typography>
                            </Box>
                          ) : wd?.weeks?.length > 0 ? (
                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              {wd.weeks.map(w => (
                                <Box key={w.key} sx={{ display: 'flex', justifyContent: 'space-between', px: 5, py: 0.5,
                                  borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                    Week ending {w.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#03DAC6' }}>
                                    {w.hours}h
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box sx={{ px: 5, py: 0.75 }}>
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>No weekly detail available</Typography>
                            </Box>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          {/* Totals row */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, px: 2, py: 1.25, bgcolor: 'rgba(255,255,255,0.03)' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{Math.round(totalAssigned * 10) / 10}h</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Billed</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: totalBilled > totalAssigned ? '#CF6679' : '#03DAC6' }}>
                {Math.round(totalBilled * 10) / 10}h
              </Typography>
            </Box>
            {totalAssigned > 0 && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Utilization</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#BB86FC' }}>
                  {Math.round((totalBilled / totalAssigned) * 100)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </BaseExpandedCard>
  );
};

const headerCell = {
  fontSize: '0.6rem',
  color: 'text.disabled',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  py: 0.75,
  fontWeight: 500
};

const bodyCell = {
  fontSize: '0.78rem',
  color: 'text.primary',
  py: 0.85
};

ProjectExpandedView.propTypes = {
  open:     PropTypes.bool.isRequired,
  onClose:  PropTypes.func.isRequired,
  project:  PropTypes.object
};

export default ProjectExpandedView;
